import React, { useState } from 'react';
import { Modal } from '@components/common';
import type { Invoice, RecordPaymentData } from '@lib/api/invoices';

interface RecordPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RecordPaymentData) => Promise<void>;
  invoice: Invoice | null;
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'credit', label: 'Credit Card' },
  { value: 'debit', label: 'Debit Card' },
  { value: 'e-transfer', label: 'E-Transfer' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'other', label: 'Other' },
];

export const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  invoice,
}) => {
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  if (!invoice) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const handleFullPayment = () => {
    setAmount(invoice.balance_due.toString());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const paymentAmount = parseFloat(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      setFormError('Please enter a valid payment amount');
      return;
    }

    if (paymentAmount > invoice.balance_due) {
      setFormError('Payment amount cannot exceed the balance due');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        amount: paymentAmount,
        payment_date: paymentDate,
        payment_method: paymentMethod || undefined,
        reference_number: referenceNumber || undefined,
        notes: notes || undefined,
      });
      // Reset form
      setAmount('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMethod('');
      setReferenceNumber('');
      setNotes('');
      onClose();
    } catch (error) {
      console.error('Failed to record payment:', error);
      setFormError('Failed to record payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record Payment"
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="payment-form"
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      }
    >
      <form id="payment-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Invoice Summary */}
        <div className="bg-[#101922] p-4 rounded-lg border border-gray-700">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-400">Invoice</span>
            <span className="text-white font-medium">{invoice.invoice_number}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-400">Total Amount</span>
            <span className="text-white">{formatCurrency(invoice.total)}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-400">Amount Paid</span>
            <span className="text-green-400">{formatCurrency(invoice.amount_paid)}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-gray-700">
            <span className="text-gray-300 font-medium">Balance Due</span>
            <span className="text-orange-400 font-semibold">{formatCurrency(invoice.balance_due)}</span>
          </div>
        </div>

        {/* Payment Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Payment Amount *
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0.01"
                max={invoice.balance_due}
                step="0.01"
                required
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-2 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 focus:border-green-500 focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={handleFullPayment}
              className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 transition-colors whitespace-nowrap"
            >
              Full Amount
            </button>
          </div>
        </div>

        {/* Payment Date */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Payment Date *
          </label>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            required
            className="w-full px-4 py-2 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 focus:border-green-500 focus:outline-none"
          />
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Payment Method
          </label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className="w-full px-4 py-2 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 focus:border-green-500 focus:outline-none"
          >
            <option value="">Select method...</option>
            {PAYMENT_METHODS.map((method) => (
              <option key={method.value} value={method.value}>
                {method.label}
              </option>
            ))}
          </select>
        </div>

        {/* Reference Number */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Reference Number
          </label>
          <input
            type="text"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            placeholder="Check #, Transaction ID, etc."
            className="w-full px-4 py-2 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 focus:border-green-500 focus:outline-none"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Optional notes about this payment..."
            className="w-full px-4 py-2 rounded-lg bg-[#101922] text-gray-100 border border-gray-700 focus:border-green-500 focus:outline-none resize-none"
          />
        </div>

        {formError && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 flex items-start gap-2 text-red-400 text-sm">
            <span className="material-symbols-outlined text-base mt-0.5 flex-shrink-0">error</span>
            <span>{formError}</span>
          </div>
        )}
      </form>
    </Modal>
  );
};
